import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import { Organization, PaymentDetails } from 'models/organization';
import { useFormik } from 'formik';
import { InvoiceDetailsSettingsFormData } from 'models/forms';
import { emptyInvoiceDetailsSettingsForm } from './form-helper';
import AddressForm from 'components/shared/address/address-form.component';
import { Address } from 'models/contact';
import { isValidEmail } from 'utils/validations';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { InvoiceDelivery, InvoicePeriod, InvoicingType } from 'models/invoice';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';
import PaymentDetailsFields from './payment-details-fields.component';
import GermanCompanyFields from './german-company-fields.component';
import MissingVatNumberWarning from 'components/shared/missing-vat-number-warning/missing-vat-number-warning.component';
import {
    FinnishReferenceFormat,
    hasFinnishPaymentFields,
    hasDutchCompanyFields,
    hasGermanCompanyFields,
    hasNorwegianPaymentFields,
    PaymentBoxAccount,
} from 'utils/payment-details-validation';
import { validatePaymentDetailsForm } from './payment-details-form-validation';

type InvoiceDetailsSettingsFormProps = {
    currentLab: Organization;
    onChange: (formData: InvoiceDetailsSettingsFormData, isValid: boolean) => void;
};

const InvoiceDetailsSettingsForm: FC<InvoiceDetailsSettingsFormProps> = ({
    currentLab,
    onChange,
}: InvoiceDetailsSettingsFormProps) => {
    const { t: translate } = useTranslation([
        'lab-settings',
        'common',
        'organization',
        'organization-numbers',
        'client-directory',
    ]);
    const currentOrg = currentOrgVar();
    const isFortnoxEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.FORTNOX,
    );
    const isVismaEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.VISMA,
    );

    const labCountry = currentLab.contact?.address?.country;
    const isNorwegianLab = hasNorwegianPaymentFields(labCountry);

    // Without a stored choice the invoice shows the bankgiro, else the plusgiro; preselect that.
    const defaultPaymentBoxAccount = (paymentDetails?: PaymentDetails) => {
        if (paymentDetails?.paymentBoxAccount) {
            return paymentDetails.paymentBoxAccount;
        }
        if (paymentDetails?.bankgiro) {
            return PaymentBoxAccount.BANKGIRO;
        }
        return paymentDetails?.postgiro ? PaymentBoxAccount.PLUSGIRO : '';
    };

    const validate = (formData: InvoiceDetailsSettingsFormData) => {
        const errors: Record<string, string> = {};
        if (formData.email && !isValidEmail(formData.email)) {
            errors.email = translate('organization:VALIDATION:INVALID_EMAIL');
        }
        Object.assign(errors, validatePaymentDetailsForm(formData, labCountry, translate));
        if (!formData.invoiceAddress || !formData.invoiceAddress.street) {
            errors.invoiceAddress = 'Address is required';
        }
        onChange(formData, Object.keys(errors).length === 0);
        return errors;
    };

    const formik = useFormik<InvoiceDetailsSettingsFormData>({
        initialValues: emptyInvoiceDetailsSettingsForm(),
        validate,
        onSubmit: async (formData) => {
            onChange(formData, formik.isValid);
        },
    });

    useEffect(() => {
        formik.setValues({
            vatNo: currentLab.invoicing?.vatNo || '',
            costCenter: currentLab.invoicing?.costCenter || '',
            invoiceAddress: currentLab.invoicing?.invoiceAddress || ({} as Address),
            email: currentLab.invoicing?.communication?.email || '',
            phone: currentLab.invoicing?.communication?.phone || '',
            site: currentLab.invoicing?.communication?.site || '',
            postgiro: currentLab.invoicing?.paymentDetails?.postgiro || '',
            bankgiro: currentLab.invoicing?.paymentDetails?.bankgiro || '',
            bicSwift: currentLab.invoicing?.paymentDetails?.bicSwift || '',
            iban: currentLab.invoicing?.paymentDetails?.iban || '',
            paymentBoxAccount: defaultPaymentBoxAccount(currentLab.invoicing?.paymentDetails),
            accountNumber: currentLab.invoicing?.paymentDetails?.accountNumber || '',
            kidEnabled: Boolean(currentLab.invoicing?.paymentDetails?.kid?.enabled),
            kidLength: currentLab.invoicing?.paymentDetails?.kid?.length?.toString() || '',
            kidCheckDigitMethod: currentLab.invoicing?.paymentDetails?.kid?.checkDigitMethod || '',
            registrationNumber: currentLab.invoicing?.paymentDetails?.registrationNumber || '',
            fiEnabled: Boolean(currentLab.invoicing?.paymentDetails?.fi?.enabled),
            fiCardType: currentLab.invoicing?.paymentDetails?.fi?.cardType || '',
            fiCreditorNumber: currentLab.invoicing?.paymentDetails?.fi?.creditorNumber || '',
            referenceFormat:
                currentLab.invoicing?.paymentDetails?.referenceFormat ||
                (hasFinnishPaymentFields(labCountry) ? FinnishReferenceFormat.NATIONAL : ''),
            latePaymentInterest: currentLab.invoicing?.latePaymentInterest?.toString() || '',
            complaintPeriodDays: currentLab.invoicing?.complaintPeriodDays?.toString() || '',
            taxNumber: currentLab.invoicing?.taxNumber || '',
            registeredOffice: currentLab.invoicing?.companyRegister?.registeredOffice || '',
            registerCourt: currentLab.invoicing?.companyRegister?.court || '',
            registerNumber: currentLab.invoicing?.companyRegister?.number || '',
            managingDirectors: currentLab.invoicing?.companyRegister?.managingDirectors || '',
            smallBusinessExemption: Boolean(currentLab.invoicing?.smallBusinessExemption),
            showForetaksregisteret: Boolean(currentLab.invoicing?.showForetaksregisteret),
            invoiceType: currentLab.invoicing?.defaultSetting?.invoiceType || '',
            invoiceDelivery: currentLab.invoicing?.defaultSetting?.invoiceDelivery || '',
            invoicePeriod: currentLab.invoicing?.defaultSetting?.invoicePeriod || '',
            paymentTerms: currentLab.invoicing?.defaultSetting?.paymentTerms || 30,
        });
    }, [currentLab]);

    const handleAddressChange = (address: Address | undefined) => {
        formik.setFieldValue('invoiceAddress', address);
    };

    const handlePaymentTermsChange = (paymentTerms: string) => {
        if (isNaN(parseInt(paymentTerms))) {
            formik.setFieldValue('paymentTerms', null);
            return;
        }
        formik.setFieldValue('paymentTerms', parseInt(paymentTerms));
    };

    return (
        <Box>
            <Box mb={2}>
                <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                    {translate('lab-settings:INVOICE_FORM_INFO')}
                </Alert>
            </Box>
            <Grid container spacing={2}>
                <Grid item sm={12} md={6}>
                    <TextField
                        fullWidth
                        label={translate('organization:COMPANY_NAME_INVOICE')}
                        InputProps={{
                            readOnly: true,
                        }}
                        value={currentLab.officialName}
                    />
                </Grid>
                <Grid item sm={12} md={6}>
                    <TextField
                        fullWidth
                        label={translate(
                            `organization-numbers:${(labCountry || 'SE').toUpperCase()}_ORGANIZATION_NUMBER`,
                        )}
                        InputProps={{
                            readOnly: true,
                        }}
                        value={currentLab.organizationNumber}
                    />
                </Grid>

                <Grid item sm={12} md={6}>
                    <FormControl fullWidth margin="dense">
                        <InputLabel id="invoice-type-label">{translate('client-directory:TYPE_OF_INVOICE')}</InputLabel>
                        <Select
                            id="invoice-type"
                            labelId="invoice-type-label"
                            label={translate('client-directory:TYPE_OF_INVOICE')}
                            {...formik.getFieldProps('invoiceType')}
                        >
                            <MenuItem value={InvoicingType.COLLECTIVE}>
                                {translate('client-directory:COLLECTIVE')}
                            </MenuItem>
                            <MenuItem value={InvoicingType.COLLECTIVE_DENTIST}>
                                {translate('client-directory:COLLECTIVE_DENTIST')}
                            </MenuItem>
                            <MenuItem value={InvoicingType.SINGLE}>{translate('client-directory:SINGLE')}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item sm={12} md={6}>
                    <FormControl fullWidth margin="dense">
                        <InputLabel>{translate('client-directory:INVOICE_DELIVERY')}</InputLabel>
                        <Select
                            id="delivery-method"
                            label={translate('client-directory:INVOICE_DELIVERY')}
                            {...formik.getFieldProps('invoiceDelivery')}
                        >
                            <MenuItem value={InvoiceDelivery.PRINT}>{translate('client-directory:PRINT')}</MenuItem>
                            <MenuItem value={InvoiceDelivery.EMAIL}>{translate('client-directory:EMAIL')}</MenuItem>
                            {isFortnoxEnabled || isVismaEnabled ? (
                                <MenuItem value={InvoiceDelivery.EXTERNAL_INVOICE_SYSTEM}>
                                    {translate('client-directory:EXTERNAL_INVOICE_SYSTEM')}
                                </MenuItem>
                            ) : undefined}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item sm={12} md={6}>
                    <FormControl fullWidth margin="dense">
                        <InputLabel>{translate('client-directory:PERIOD')}</InputLabel>
                        <Select
                            id="invoice-period"
                            disabled={!formik.getFieldProps('invoiceType').value}
                            label={translate('client-directory:PERIOD')}
                            {...formik.getFieldProps('invoicePeriod')}
                        >
                            <MenuItem value={InvoicePeriod.MONTHLY}>{translate('client-directory:MONTHLY')}</MenuItem>
                            <MenuItem value={InvoicePeriod.SEMI_WEEKLY}>
                                {translate('client-directory:SEMI_WEEKLY')}
                            </MenuItem>
                            <MenuItem value={InvoicePeriod.WEEKLY}>{translate('client-directory:WEEKLY')}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item sm={12} md={6}>
                    <FormControl fullWidth>
                        <NumberOnlyTextField
                            hasDecimal={false}
                            label={translate('client-directory:PAYMENT_TERMS')}
                            onValueChange={handlePaymentTermsChange}
                            onBlur={formik.handleBlur}
                            name={formik.getFieldProps('paymentTerms').name}
                            value={formik.getFieldProps('paymentTerms').value}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {translate('client-directory:DAYS').toLowerCase()}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </FormControl>
                </Grid>

                <Grid item sm={12} md={12}>
                    <AddressForm address={currentLab.invoicing?.invoiceAddress} onChange={handleAddressChange} />
                </Grid>

                <Grid item sm={12} md={4}>
                    <TextField
                        fullWidth
                        label={translate('common:EMAIL')}
                        required
                        helperText={formik.touched.email && formik.errors.email}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        {...formik.getFieldProps('email')}
                    />
                </Grid>
                <Grid item sm={12} md={4}>
                    <TextField fullWidth label={translate('common:PHONE')} {...formik.getFieldProps('phone')} />
                </Grid>
                <Grid item sm={12} md={4}>
                    <TextField fullWidth label={translate('common:WEBSITE')} {...formik.getFieldProps('site')} />
                </Grid>

                <Grid item xs={12}>
                    <MissingVatNumberWarning showSettingsLink={false} />
                </Grid>
                <Grid item sm={12} md={6}>
                    <TextField
                        fullWidth
                        label={translate('lab-settings:VAT_NUMBER')}
                        {...formik.getFieldProps('vatNo')}
                    />
                </Grid>
                <Grid item sm={12} md={6}>
                    <TextField
                        label={translate('lab-settings:REFERENCE')}
                        fullWidth
                        {...formik.getFieldProps('costCenter')}
                    />
                </Grid>
                {hasGermanCompanyFields(labCountry) ? <GermanCompanyFields formik={formik} /> : undefined}
                {hasDutchCompanyFields(labCountry) ? (
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    {...formik.getFieldProps({ name: 'smallBusinessExemption', type: 'checkbox' })}
                                />
                            }
                            label={translate('lab-settings:SMALL_BUSINESS_EXEMPTION_NL')}
                        />
                        <FormHelperText>{translate('lab-settings:SMALL_BUSINESS_EXEMPTION_NL_INFO')}</FormHelperText>
                    </Grid>
                ) : undefined}
                {isNorwegianLab ? (
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    {...formik.getFieldProps({ name: 'showForetaksregisteret', type: 'checkbox' })}
                                />
                            }
                            label={translate('lab-settings:SHOW_FORETAKSREGISTERET')}
                        />
                        <FormHelperText>{translate('lab-settings:SHOW_FORETAKSREGISTERET_INFO')}</FormHelperText>
                    </Grid>
                ) : undefined}
            </Grid>
            <PaymentDetailsFields formik={formik} country={labCountry} />
        </Box>
    );
};

export default InvoiceDetailsSettingsForm;
